import React from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  LinearProgress,
  Avatar,
  Stack
} from '@mui/material';
import {
  Schedule,
  People,
  LocalOffer
} from '@mui/icons-material';
import { Campaign } from '../types';

interface CampaignCardProps {
  campaign: Campaign;
  onClick?: () => void;
}

const CampaignCard: React.FC<CampaignCardProps> = ({ campaign, onClick }) => {
  const progressPercentage = campaign.maxParticipants 
    ? (campaign.participantCount / campaign.maxParticipants) * 100 
    : 0;

  const daysLeft = Math.ceil((campaign.deadline.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'product':
        return '#4CAF50';
      case 'place':
        return '#2196F3';
      case 'service':
        return '#FF9800';
      default:
        return '#9E9E9E';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'popular':
        return '인기';
      case 'new':
        return '신규';
      case 'ongoing':
        return '진행중';
      default:
        return '';
    }
  };

  return (
    <Card 
      sx={{ 
        width: '100%',
        maxWidth: { xs: '100%', sm: 345 },
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 3
        }
      }}
      onClick={onClick}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="200"
          image={campaign.imageUrl}
          alt={campaign.title}
          sx={{
            height: { xs: 160, sm: 200 }
          }}
        />
        
        {/* 타입 배지 */}
        <Chip
          label={getTypeLabel(campaign.type)}
          color={campaign.type === 'popular' ? 'error' : campaign.type === 'new' ? 'success' : 'primary'}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            fontWeight: 'bold'
          }}
        />

        {/* 카테고리 배지 */}
        <Chip
          label={campaign.category === 'product' ? '상품' : campaign.category === 'place' ? '플레이스' : '서비스'}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: getCategoryColor(campaign.category),
            color: 'white'
          }}
        />
      </Box>

      <CardContent>
        {/* 브랜드 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '12px' }}>
            {campaign.brand.charAt(0)}
          </Avatar>
          <Typography variant="caption" color="text.secondary">
            {campaign.brand}
          </Typography>
        </Box>

        {/* 제목 */}
        <Typography 
          variant="h6" 
          component="h2" 
          sx={{ 
            fontWeight: 'bold', 
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {campaign.title}
        </Typography>

        {/* 설명 */}
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {campaign.description}
        </Typography>

        {/* 리워드 정보 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LocalOffer sx={{ color: '#FF9800', mr: 1, fontSize: '20px' }} />
          <Typography variant="body2" color="primary" fontWeight="bold">
            {campaign.reward.points.toLocaleString()}P
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {campaign.reward.description}
          </Typography>
        </Box>

        {/* 참여자 정보 */}
        {campaign.maxParticipants && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <People sx={{ fontSize: '16px', mr: 0.5 }} />
                <Typography variant="caption">
                  {campaign.participantCount}/{campaign.maxParticipants}명
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {progressPercentage.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progressPercentage} 
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}

        {/* 마감일 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Schedule sx={{ fontSize: '16px', mr: 0.5, color: daysLeft <= 3 ? 'error.main' : 'text.secondary' }} />
          <Typography 
            variant="caption" 
            color={daysLeft <= 3 ? 'error.main' : 'text.secondary'}
            fontWeight={daysLeft <= 3 ? 'bold' : 'normal'}
          >
            {daysLeft > 0 ? `${daysLeft}일 남음` : '마감'}
          </Typography>
        </Box>

        {/* 태그 */}
        <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
          {campaign.tags.slice(0, 3).map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ fontSize: '10px', height: '20px' }}
            />
          ))}
        </Stack>

        {/* 참여 버튼 */}
        <Button
          variant="contained"
          fullWidth
          disabled={campaign.status !== 'active' || Boolean(campaign.maxParticipants && campaign.participantCount >= campaign.maxParticipants)}
          sx={{
            fontWeight: 'bold',
            py: 1
          }}
        >
          {campaign.status !== 'active' 
            ? '마감됨' 
            : (campaign.maxParticipants && campaign.participantCount >= campaign.maxParticipants)
              ? '참여 마감'
              : '참여하기'
          }
        </Button>
      </CardContent>
    </Card>
  );
};

export default CampaignCard; 